// Mirrors CF_CATEGORIES in the scheduler; the frontend is still on contento-common 3.x, which
// predates the shared content-factory types.
export const CF_CATEGORIES = ["educational", "workplace", "entertaining", "promotional"] as const

export type CfCategory = (typeof CF_CATEGORIES)[number]

export type ChannelSelection = {
  channelId: string
  allowedCategories: CfCategory[]
}

const CATEGORY_LABELS: Record<CfCategory, string> = {
  educational: "Educational",
  workplace: "Workplace",
  entertaining: "Entertaining",
  promotional: "Promotional",
}

export const categoryLabel = (category: CfCategory): string => CATEGORY_LABELS[category]

export const summarizeCategories = (allowedCategories: CfCategory[]): string => {
  if (allowedCategories.length >= CF_CATEGORIES.length) return "All categories"
  if (allowedCategories.length === 1) return `${categoryLabel(allowedCategories[0])} only`
  return `${allowedCategories.length} categories`
}

// Kept in CF_CATEGORIES order so the payload and the summary don't shift with click order.
export const toggleCategory = (allowedCategories: CfCategory[], category: CfCategory): CfCategory[] => {
  const next = allowedCategories.includes(category)
    ? allowedCategories.filter((allowed) => allowed !== category)
    : [...allowedCategories, category]
  return CF_CATEGORIES.filter((known) => next.includes(known))
}

// Adding a channel starts it on every category; removing it drops the entry entirely.
export const toggleChannelSelection = (
  channelSelections: ChannelSelection[],
  channelId: string,
): ChannelSelection[] =>
  channelSelections.some((selection) => selection.channelId === channelId)
    ? channelSelections.filter((selection) => selection.channelId !== channelId)
    : [...channelSelections, { channelId, allowedCategories: [...CF_CATEGORIES] }]

// A channel left with no category publishes nothing, so it drops out of the selection.
export const toggleCategorySelection = (
  channelSelections: ChannelSelection[],
  channelId: string,
  category: CfCategory,
): ChannelSelection[] =>
  channelSelections
    .map((selection) => selection.channelId === channelId
      ? { ...selection, allowedCategories: toggleCategory(selection.allowedCategories, category) }
      : selection)
    .filter((selection) => selection.allowedCategories.length > 0)
